import { WHITE_LABEL_ID } from "config";
import axios from "utils/axios";

/**
 * Servicio para adoptar la identidad visual de white labels
 *
 * El panel adopta automáticamente la identidad del white label basado en:
 * - ID del white label definido en variables de entorno (REACT_APP_WHITE_LABEL_ID)
 * - Dominio/host actual (detectado automáticamente como fallback)
 * - ID del negocio seleccionado (solo para casos específicos)
 *
 * ENDPOINT UNIFICADO EN LARAVEL:
 *
 * GET /api/branding - Endpoint público que resuelve automáticamente basado en:
 *   1. ?white_label_id=X - Configuración específica del white label (NUEVO)
 *   2. ?negocio_id=X - Configuración específica del negocio (DEPRECATED)
 *   3. Header X-Brand-Host - Configuración por dominio personalizado
 *   4. Host automático - Detecta el dominio de la petición
 *
 * El panel solo consume la configuración, no la gestiona.
 */
class BrandingService {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 3600; // 1 hora en segundos
    this.currentBranding = null; // Para referencia en otros métodos
  }

  /**
   * Carga y aplica la configuración de branding al inicio de la aplicación
   * @param {number} negocioId - ID del negocio (opcional, usado solo para compatibilidad)
   * @returns {Promise<Object>} Configuración de branding aplicada
   */
  async loadAndApplyBranding(negocioId = null) {
    try {
      console.log("🎨 Loading branding configuration...");

      // Priorizar WHITE_LABEL_ID de variables de entorno
      let branding;
      if (WHITE_LABEL_ID) {
        console.log(`📋 Using WHITE_LABEL_ID from environment: ${WHITE_LABEL_ID}`);
        branding = await this.forWhiteLabel(WHITE_LABEL_ID);
      } else if (negocioId) {
        console.log(`📋 Using negocio_id (legacy): ${negocioId}`);
        branding = await this.forNegocio(negocioId);
      } else {
        console.log("📋 Using automatic host detection");
        branding = await this.getBranding();
      }

      // Guardar referencia actual
      this.currentBranding = branding;

      // Aplicar configuración al DOM
      this.applyBranding(branding);

      console.log("✅ Branding applied successfully:", {
        brand: branding.white_label?.name || "Default",
        whiteLabelId: branding.white_label?.id,
        hasLogo: !!branding.branding?.logo_url,
        hasFavicon: !!branding.branding?.favicon_url,
        hasCustomCSS: !!branding.branding?.theme?.custom_css,
        colors: {
          primary: branding.branding?.primary_color,
          secondary: branding.branding?.secondary_color,
        },
      });

      return branding;
    } catch (error) {
      console.error("❌ Error loading branding configuration:", error);

      // Aplicar configuración por defecto en caso de error
      const defaultBranding = this.getDefaultBranding();
      this.currentBranding = defaultBranding;
      this.applyBranding(defaultBranding);

      return defaultBranding;
    }
  }

  /**
   * Obtiene la configuración de branding para un white label específico
   * @param {number} whiteLabelId - ID del white label
   * @returns {Promise<Object>} Configuración de branding
   */
  async forWhiteLabel(whiteLabelId) {
    const cacheKey = `branding:white_label:${whiteLabelId}`;

    // Verificar caché
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.defaultTTL * 1000) {
        return cached.data;
      }
    }

    try {
      // Obtener la configuración del white label usando el endpoint unificado
      const response = await axios.get("/branding", {
        params: { white_label_id: whiteLabelId },
      });
      const brandingData = response.data;

      // Guardar en caché
      this.cache.set(cacheKey, {
        data: brandingData,
        timestamp: Date.now(),
      });

      return brandingData;
    } catch (error) {
      console.error("Error fetching branding for white label:", error);
      return this.getDefaultBranding();
    }
  }

  /**
   * Obtiene la configuración de branding para un negocio (DEPRECATED)
   * @param {number} negocioId - ID del negocio
   * @returns {Promise<Object>} Configuración de branding
   */
  async forNegocio(negocioId) {
    const cacheKey = `branding:negocio:${negocioId}`;

    // Verificar caché
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.defaultTTL * 1000) {
        return cached.data;
      }
    }

    try {
      // Obtener el white label asignado al negocio usando el endpoint unificado
      const response = await axios.get("/branding", {
        params: { negocio_id: negocioId },
      });
      const brandingData = response.data;

      // Guardar en caché
      this.cache.set(cacheKey, {
        data: brandingData,
        timestamp: Date.now(),
      });

      return brandingData;
    } catch (error) {
      console.error("Error fetching branding for negocio:", error);
      return this.getDefaultBranding();
    }
  }

  /**
   * Obtiene la configuración de branding por host/dominio
   * @param {string} host - Dominio (ej: app.itau.com)
   * @returns {Promise<Object>} Configuración de branding
   */
  async forHost(host) {
    const cacheKey = `branding:host:${host}`;

    // Verificar caché
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.defaultTTL * 1000) {
        return cached.data;
      }
    }

    try {
      // Buscar white label por dominio usando header X-Brand-Host
      const response = await axios.get("/branding", {
        headers: { "X-Brand-Host": host },
      });

      const brandingData = response.data;

      // Guardar en caché
      this.cache.set(cacheKey, {
        data: brandingData,
        timestamp: Date.now(),
      });

      return brandingData;
    } catch (error) {
      console.error("Error fetching branding for host:", error);
      return this.getDefaultBranding();
    }
  }

  /**
   * Obtiene la configuración de branding automáticamente
   * @param {number} negocioId - ID del negocio (opcional, legacy)
   * @returns {Promise<Object>} Configuración de branding
   */
  async getBranding(negocioId = null) {
    // Priorizar WHITE_LABEL_ID de variables de entorno
    if (WHITE_LABEL_ID) {
      return this.forWhiteLabel(WHITE_LABEL_ID);
    }

    // Si se proporciona negocioId, obtener directamente para ese negocio (legacy)
    if (negocioId) {
      return this.forNegocio(negocioId);
    }

    // Usar endpoint unificado para detección automática por host
    const cacheKey = "branding:auto";

    // Verificar caché
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.defaultTTL * 1000) {
        return cached.data;
      }
    }

    try {
      // El endpoint /branding detecta automáticamente el host
      const response = await axios.get("/branding");
      const brandingData = response.data;

      // Guardar en caché
      this.cache.set(cacheKey, {
        data: brandingData,
        timestamp: Date.now(),
      });

      return brandingData;
    } catch (error) {
      console.warn("No white label found for host, using default branding");
      return this.getDefaultBranding();
    }
  }

  /**
   * Retorna la configuración por defecto cuando no hay white label
   * @returns {Object} Configuración por defecto
   */
  getDefaultBranding() {
    return {
      white_label: {
        id: null,
        name: "Rhino",
        slug: "rhino",
      },
      branding: {
        primary_color: "#2296f3",
        secondary_color: "#f50057",
        logo_url: "/favicon.png",
        favicon_url: "/favicon.png",
        theme: {
          buttons: {
            radius: 16,
          },
          fontFamily: "'Poppins', sans-serif",
          borderRadius: 16,
        },
      },
    };
  }

  /**
   * Invalida el caché para un negocio y/o white label específico
   * @param {number} negocioId - ID del negocio
   * @param {number} whiteLabelId - ID del white label
   */
  invalidate(negocioId = null, whiteLabelId = null) {
    if (negocioId) {
      this.cache.delete(`branding:negocio:${negocioId}`);
    }

    if (whiteLabelId) {
      // Eliminar todas las entradas de caché relacionadas con el white label
      for (const key of this.cache.keys()) {
        if (key.includes(`whitelabel:${whiteLabelId}`)) {
          this.cache.delete(key);
        }
      }
    }
  }

  /**
   * Limpia todo el caché
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Refresca la configuración de branding (limpia caché y recarga)
   * @param {number} negocioId - ID del negocio (opcional, legacy)
   * @returns {Promise<Object>} Nueva configuración de branding
   */
  async refreshBranding(negocioId = null) {
    console.log("🔄 Refreshing branding configuration...");

    // Limpiar caché relacionado basado en la prioridad
    if (WHITE_LABEL_ID) {
      this.cache.delete(`branding:white_label:${WHITE_LABEL_ID}`);
    } else if (negocioId) {
      this.invalidate(negocioId);
    } else {
      this.cache.delete("branding:auto");
    }

    // Cargar y aplicar nueva configuración
    return await this.loadAndApplyBranding(negocioId);
  }

  /**
   * Obtiene la configuración de branding actualmente aplicada
   * @returns {Object|null} Configuración actual de branding
   */
  getCurrentBranding() {
    return this.currentBranding;
  }

  /**
   * Aplica la configuración de branding al DOM
   * @param {Object} branding - Configuración de branding
   */
  applyBranding(branding) {
    console.log("🎨 Applying branding configuration:", branding);

    // Manejar estructura {data: {...}} que viene del API
    let brandingData = branding;
    if (branding && branding.data) {
      console.log("📦 Extracting data from wrapper");
      brandingData = branding.data;
    }

    if (!brandingData || !brandingData.branding) {
      console.warn("⚠️ No branding configuration found. Structure:", brandingData);
      return;
    }

    const { branding: brandConfig } = brandingData;
    console.log("🎨 Brand config extracted:", brandConfig);

    // Actualizar colores CSS personalizados
    if (brandConfig.primary_color) {
      console.log("🔵 Applying primary color:", brandConfig.primary_color);
      document.documentElement.style.setProperty("--white-label-primary", brandConfig.primary_color);
      document.documentElement.style.setProperty("--primary-color", brandConfig.primary_color);
      // También aplicar a variables específicas de Material-UI
      document.documentElement.style.setProperty("--mui-palette-primary-main", brandConfig.primary_color);
      document.documentElement.style.setProperty("--palette-primary-main", brandConfig.primary_color);
    }

    if (brandConfig.secondary_color) {
      console.log("🔴 Applying secondary color:", brandConfig.secondary_color);
      document.documentElement.style.setProperty("--white-label-secondary", brandConfig.secondary_color);
      document.documentElement.style.setProperty("--secondary-color", brandConfig.secondary_color);
      // También aplicar a variables específicas de Material-UI
      document.documentElement.style.setProperty("--mui-palette-secondary-main", brandConfig.secondary_color);
      document.documentElement.style.setProperty("--palette-secondary-main", brandConfig.secondary_color);
    }

    // Aplicar logo
    if (brandConfig.logo_url) {
      console.log("🖼️ Applying logo:", brandConfig.logo_url);
      this.updateLogo(brandConfig.logo_url);
    }

    // Actualizar favicon
    if (brandConfig.favicon_url) {
      console.log("📄 Applying favicon:", brandConfig.favicon_url);
      this.updateFavicon(brandConfig.favicon_url);
    }

    // Aplicar CSS personalizado
    if (brandConfig.theme && brandConfig.theme.custom_css) {
      console.log("📝 Applying custom CSS");
      this.applyCustomCSS(brandConfig.theme.custom_css);
    }

    // Actualizar título si está disponible
    if (brandingData.white_label && brandingData.white_label.name) {
      console.log("📰 Updating title:", brandingData.white_label.name);
      document.title = `${brandingData.white_label.name} - Dashboard Administración`;
    }

    console.log("✅ Branding applied successfully");
  }

  /**
   * Actualiza el favicon del sitio
   * @param {string} faviconUrl - URL del favicon
   */
  updateFavicon(faviconUrl) {
    let link = document.querySelector("link[rel='icon']") || document.querySelector("link[rel='shortcut icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      link.type = "image/png";
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }

  /**
   * Actualiza el logo del sitio
   * @param {string} logoUrl - URL del logo
   */
  updateLogo(logoUrl) {
    // Buscar elementos de logo comunes
    const logoSelectors = [".logo img", ".brand-logo img", "[data-logo] img", ".navbar-brand img", ".header-logo img"];

    logoSelectors.forEach((selector) => {
      const logoElement = document.querySelector(selector);
      if (logoElement) {
        logoElement.src = logoUrl;
        logoElement.alt = `${this.currentBranding?.white_label?.name || "Brand"} Logo`;
      }
    });

    // También actualizar propiedad CSS personalizada para logos
    document.documentElement.style.setProperty("--brand-logo-url", `url(${logoUrl})`);
  }

  /**
   * Aplica CSS personalizado al documento
   * @param {string} customCSS - CSS personalizado
   */
  applyCustomCSS(customCSS) {
    // Remover CSS personalizado anterior si existe
    const existingStyle = document.getElementById("white-label-custom-css");
    if (existingStyle) {
      existingStyle.remove();
    }

    // Crear nuevo elemento de estilo
    if (customCSS && customCSS.trim()) {
      const style = document.createElement("style");
      style.id = "white-label-custom-css";
      style.textContent = customCSS;
      document.head.appendChild(style);
    }
  }

  /**
   * Verifica si se debe usar configuración Handy (compatibilidad hacia atrás)
   * @param {Object} branding - Configuración de branding
   * @returns {boolean}
   */
  isHandyBrand(branding) {
    return branding?.white_label?.slug === "handy";
  }

  /**
   * Obtiene la configuración de tema basada en el branding
   * @param {Object} branding - Configuración de branding
   * @returns {Object} Configuración de tema
   */
  getThemeConfig(branding) {
    const defaultConfig = {
      fontFamily: "'Poppins', sans-serif",
      borderRadius: 16,
      presetColor: "default",
      outlinedFilled: true,
      navType: "light",
      locale: "es",
      rtlLayout: false,
      container: false,
    };

    if (!branding?.branding?.theme) {
      return defaultConfig;
    }

    const { theme } = branding.branding;

    return {
      ...defaultConfig,
      fontFamily: theme.fontFamily || defaultConfig.fontFamily,
      borderRadius: theme.buttons?.radius || defaultConfig.borderRadius,
      presetColor: this.isHandyBrand(branding) ? "theme7" : defaultConfig.presetColor,
      outlinedFilled: true,
      navType: "light",
      locale: "es",
      rtlLayout: false,
      container: false,
    };
  }
}

// Instancia singleton del servicio
export const brandingService = new BrandingService();

export default brandingService;
